import { useCallback, useEffect, useRef, useState } from 'react';
import { clampDockWidth, getViewportSize } from './floatingPanelGeometry';

const getClientX = (event) => event.clientX ?? 0;

export const useDockPanel = ({
	enabled,
	bounds,
	defaultWidth
}) => {
	const [dockWidth, setDockWidth] = useState(() => clampDockWidth(defaultWidth, {
		...bounds,
		viewport: getViewportSize()
	}));
	const [interaction, setInteraction] = useState(null);
	const dockWidthRef = useRef(dockWidth);
	dockWidthRef.current = dockWidth;

	const isInteracting = Boolean(interaction);

	const resetDockWidth = useCallback(() => {
		const viewport = getViewportSize();
		setDockWidth(clampDockWidth(defaultWidth, { ...bounds, viewport }));
	}, [bounds, defaultWidth]);

	useEffect(() => {
		if (!enabled) {
			return;
		}
		const onResize = () => {
			const viewport = getViewportSize();
			setDockWidth((current) => clampDockWidth(current, { ...bounds, viewport }));
		};
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, [enabled, bounds]);

	useEffect(() => {
		if (!interaction) {
			return undefined;
		}
		const onPointerMove = (event) => {
			event.preventDefault();
			const viewport = getViewportSize();
			const deltaX = getClientX(event) - interaction.startClientX;
			const nextWidth = interaction.startWidth - deltaX;
			setDockWidth(clampDockWidth(nextWidth, { ...bounds, viewport }));
		};
		const onPointerUp = (event) => {
			event.preventDefault();
			setInteraction(null);
		};
		document.addEventListener('pointermove', onPointerMove);
		document.addEventListener('pointerup', onPointerUp);
		document.addEventListener('pointercancel', onPointerUp);
		return () => {
			document.removeEventListener('pointermove', onPointerMove);
			document.removeEventListener('pointerup', onPointerUp);
			document.removeEventListener('pointercancel', onPointerUp);
		};
	}, [interaction, bounds]);

	const startLeftResize = useCallback((event) => {
		if (!enabled) {
			return;
		}
		if (event.button != null && event.button !== 0) {
			return;
		}
		event.preventDefault();
		event.stopPropagation();
		setInteraction({
			startClientX: getClientX(event),
			startWidth: dockWidthRef.current
		});
	}, [enabled]);

	return {
		dockWidth,
		isInteracting,
		resetDockWidth,
		setDockWidth: (width) => {
			const viewport = getViewportSize();
			setDockWidth(clampDockWidth(width, { ...bounds, viewport }));
		},
		startLeftResize
	};
};
